import React, {useCallback, useEffect, useState} from 'react';
import {
    Badge, Button, Card, Col, Input, Modal,
    Row, Space, Statistic, Table, Tag, Typography, message,
} from 'antd';
import {
    AlertOutlined, ArrowRightOutlined, DollarOutlined,
    FileTextOutlined, ShoppingOutlined, UserOutlined,
} from '@ant-design/icons';
import {
    CartesianGrid, ComposedChart, Legend, Line, Bar,
    ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import moment from 'moment';
import api from '../services/api';
import ENDPOINTS from '../constants/endpoints';
import {formatPath, ROUTES} from '../constants/PathRoutes';
import {moneyMask, dateFormat, getStartMonth, getEndMonth} from '../utils/utils';
import {GREE_1, ORDER_STATUS_COLOR} from '../utils/colors';
import {useNavigate} from 'react-router-dom';
import {useAdminAuth} from '../contexts/AdminAuthContext';
import {PERMISSIONS} from '../constants/permissions';
import ReportDateRangeFilter from '../components/admin/reports/ReportDateRangeFilter';

const STATUS_LABEL = {
    PENDING_PAYMENT: 'Ag. Pagamento',
    PAID: 'Pago',
    PREPARING: 'Em Preparo',
    SHIPPED: 'Enviado',
    DELIVERED: 'Entregue',
    CANCELLED: 'Cancelado',
};

const STATUS_NEXT = {
    PAID: 'PREPARING',
    PREPARING: 'SHIPPED',
    SHIPPED: 'DELIVERED',
};

const STATUS_NEXT_LABEL = {
    PAID: 'Em Preparo',
    PREPARING: 'Enviado',
    SHIPPED: 'Entregue',
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const {hasPermission} = useAdminAuth();
    const canManageClients = hasPermission(PERMISSIONS.CLIENT_MANAGE);
    const canManagePrescriptions = hasPermission(PERMISSIONS.PRESCRIPTION_MANAGE);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState([getStartMonth(), getEndMonth()]);

    // Quick action state
    const [actionLoading, setActionLoading] = useState(null);
    const [trackingModal, setTrackingModal] = useState(false);
    const [trackingOrder, setTrackingOrder] = useState(null);
    const [trackingCode, setTrackingCode] = useState('');

    const fetchDashboard = useCallback(async (range) => {
        setLoading(true);
        try {
            const params = {};
            if (range?.[0] && range?.[1]) {
                params.date_from = range[0].format('YYYY-MM-DD');
                params.date_to = range[1].format('YYYY-MM-DD');
            }
            const res = await api.get(ENDPOINTS.ECOMMERCE_DASHBOARD, {params});
            setData(res.data);
        } catch {
            message.error('Erro ao carregar dashboard');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboard(null);
    }, [fetchDashboard]);

    const handleDateApply = () => fetchDashboard(dateRange);

    const handleQuickAction = (order) => {
        const nextStatus = STATUS_NEXT[order.status];
        if (!nextStatus) return;
        if (nextStatus === 'SHIPPED') {
            setTrackingOrder(order);
            setTrackingCode('');
            setTrackingModal(true);
        } else {
            submitAction(order.id, nextStatus);
        }
    };

    const submitAction = async (orderId, nextStatus, extraBody = {}) => {
        setActionLoading(orderId);
        try {
            await api.post(
                formatPath(ENDPOINTS.ECOMMERCE_STAFF_ORDER_UPDATE_STATUS, {id: orderId}),
                {status: nextStatus, ...extraBody}
            );
            message.success('Status atualizado');
            fetchDashboard(dateRange);
        } catch {
            message.error('Erro ao atualizar status');
        } finally {
            setActionLoading(null);
        }
    };

    const handleTrackingConfirm = () => {
        if (!trackingCode.trim()) {
            message.warning('Informe o código de rastreio');
            return;
        }
        setTrackingModal(false);
        submitAction(trackingOrder.id, 'SHIPPED', {tracking_code: trackingCode.trim()});
    };

    const summary = data?.summary ?? {};
    const ordersByStatus = summary.orders_by_status ?? {};
    const evolution = data?.evolution ?? [];
    const pendingAction = data?.pending_action ?? [];
    const bestSellers = data?.best_sellers ?? [];
    const period = data?.period;

    const evolutionData = evolution.map(e => ({
        ...e,
        date: moment(e.date).format('DD/MM'),
        revenue: Number(e.revenue),
    }));

    const pendingColumns = [
        {title: 'Pedido', dataIndex: 'order_number', key: 'order_number'},
        {title: 'Cliente', dataIndex: 'client_name', key: 'client_name', render: v => v || '-'},
        {title: 'Data', dataIndex: 'created_at', key: 'created_at', render: (date) => dateFormat(date)},
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={ORDER_STATUS_COLOR[status] ?? 'default'}>{STATUS_LABEL[status] ?? status}</Tag>
            ),
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            render: v => `R$ ${moneyMask(v)}`,
        },
        {
            title: 'Ações',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    {/*{STATUS_NEXT[record.status] && (*/}
                    {/*    <Button*/}
                    {/*        size="small"*/}
                    {/*        type="primary"*/}
                    {/*        loading={actionLoading === record.id}*/}
                    {/*        onClick={() => handleQuickAction(record)}*/}
                    {/*    >*/}
                    {/*        {STATUS_NEXT_LABEL[record.status]}*/}
                    {/*    </Button>*/}
                    {/*)}*/}
                    <Button
                        size="small"
                        icon={<ArrowRightOutlined/>}
                        onClick={() => navigate(formatPath(ROUTES.ADMIN_ECOMMERCE_ORDER_DETAIL, {id: record.id}))}
                    />
                </Space>
            ),
        },
    ];

    const bestSellersColumns = [
        {title: 'Produto', dataIndex: 'product_name', key: 'product_name'},
        {title: 'Qtd. Vendida', dataIndex: 'qty_sold', key: 'qty_sold', align: 'right'},
        {
            title: 'Receita',
            dataIndex: 'revenue',
            key: 'revenue',
            align: 'right',
            render: v => `R$ ${moneyMask(v)}`,
        },
    ];

    return (
        <div>
            {/* Header + filtro de data */}
            <Row justify="space-between" align="middle" style={{marginBottom: 24}} wrap>
                <Col>
                    <Typography.Title level={4} style={{margin: 0}}>Dashboard</Typography.Title>
                    {period && !loading && (
                        <Typography.Text type="secondary" style={{fontSize: 12}}>
                            {moment(period.date_from).format('DD/MM/YYYY')} – {moment(period.date_to).format('DD/MM/YYYY')}
                        </Typography.Text>
                    )}
                </Col>
                <Col>
                    <ReportDateRangeFilter
                        value={dateRange}
                        onChange={setDateRange}
                        onApply={handleDateApply}
                        loading={loading}
                    />
                </Col>
            </Row>

            {/* Cards de resumo */}
            <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                <Col xs={24} sm={12} lg={6}>
                    <Card loading={loading}>
                        <Statistic
                            title="Receita"
                            value={summary.revenue ?? 0}
                            prefix="R$"
                            precision={2}
                            formatter={v => moneyMask(v)}
                            valueStyle={{color: GREE_1}}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card loading={loading}>
                        <Statistic
                            title="Ticket Médio"
                            value={summary.avg_ticket ?? 0}
                            prefix="R$"
                            precision={2}
                            formatter={v => moneyMask(v)}
                            valueStyle={{color: '#1890ff'}}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        loading={loading}
                        hoverable={canManageClients && !!summary.pending_clients}
                        onClick={() => canManageClients && summary.pending_clients && navigate(ROUTES.ADMIN_CLIENT_LIST)}
                    >
                        <Statistic
                            title={
                                <Space>
                                    <UserOutlined/>
                                    Clientes Pendentes
                                </Space>
                            }
                            value={summary.pending_clients ?? 0}
                            valueStyle={{color: summary.pending_clients ? '#faad14' : undefined}}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        loading={loading}
                        hoverable={canManagePrescriptions && !!summary.pending_prescriptions}
                        onClick={() => canManagePrescriptions && summary.pending_prescriptions && navigate(ROUTES.ADMIN_ECOMMERCE_PRESCRIPTION_LIST)}
                    >
                        <Statistic
                            title={
                                <Space>
                                    <FileTextOutlined/>
                                    Prescrições Pendentes
                                </Space>
                            }
                            value={summary.pending_prescriptions ?? 0}
                            valueStyle={{color: summary.pending_prescriptions ? '#faad14' : undefined}}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Pedidos por status */}
            <Card
                loading={loading}
                title={<Space><ShoppingOutlined/> Pedidos por Status</Space>}
                style={{marginBottom: 16}}
            >
                <Row gutter={[16, 8]}>
                    {Object.entries(ordersByStatus).map(([status, count]) => (
                        <Col key={status} xs={12} sm={8} md={4}>
                            <div style={{textAlign: 'center'}}>
                                <Badge
                                    count={count}
                                    showZero
                                    color={ORDER_STATUS_COLOR[status] ?? '#d9d9d9'}
                                    style={{fontSize: 13}}
                                    overflowCount={999}
                                />
                                <div style={{marginTop: 6, fontSize: 12, color: '#666'}}>
                                    {STATUS_LABEL[status] ?? status}
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            </Card>

            {/* Gráfico de evolução */}
            <Card
                loading={loading}
                title="Evolução do Período"
                style={{marginBottom: 16}}
            >
                <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={evolutionData} margin={{top: 4, right: 24, left: 0, bottom: 4}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                        <XAxis dataKey="date" tick={{fontSize: 12}}/>
                        <YAxis yAxisId="left" tick={{fontSize: 12}} allowDecimals={false}/>
                        <YAxis yAxisId="right" orientation="right" tick={{fontSize: 12}} tickFormatter={v => `R$${v}`}/>
                        <Tooltip
                            formatter={(value, name) =>
                                name === 'Receita' ? [`R$ ${moneyMask(value)}`, name] : [value, name]
                            }
                        />
                        <Legend/>
                        <Bar yAxisId="left" dataKey="orders" name="Pedidos" fill="#91caff" radius={[3, 3, 0, 0]}/>
                        <Line yAxisId="right" type="monotone" dataKey="revenue" name="Receita" stroke={GREE_1} strokeWidth={2} dot={false}/>
                    </ComposedChart>
                </ResponsiveContainer>
            </Card>

            {/* Pending action + best sellers */}
            <Row gutter={[16, 16]}>
                <Col xs={24} xl={14}>
                    <Card
                        title={<Space><AlertOutlined style={{color: '#faad14'}}/> Pedidos Aguardando Ação</Space>}
                        loading={loading}
                    >
                        <Table
                            dataSource={pendingAction}
                            columns={pendingColumns}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            locale={{emptyText: 'Nenhum pedido pendente'}}
                            scroll={{x: 'max-content'}}
                        />
                    </Card>
                </Col>
                <Col xs={24} xl={10}>
                    <Card
                        title={<Space><DollarOutlined/> Mais Vendidos</Space>}
                        loading={loading}
                    >
                        <Table
                            dataSource={bestSellers}
                            columns={bestSellersColumns}
                            rowKey="product_name"
                            pagination={false}
                            size="small"
                            locale={{emptyText: 'Sem dados'}}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Modal código de rastreio */}
            <Modal
                title="Código de Rastreio"
                open={trackingModal}
                onOk={handleTrackingConfirm}
                onCancel={() => setTrackingModal(false)}
                okText="Confirmar"
                cancelText="Cancelar"
                confirmLoading={actionLoading === trackingOrder?.id}
            >
                <Space direction="vertical" style={{width: '100%', marginTop: 8}}>
                    <Typography.Text>Informe o código de rastreio para marcar o pedido como Enviado:</Typography.Text>
                    <Input
                        placeholder="Ex: BR123456789BR"
                        value={trackingCode}
                        onChange={e => setTrackingCode(e.target.value)}
                        onPressEnter={handleTrackingConfirm}
                        autoFocus
                    />
                </Space>
            </Modal>
        </div>
    );
}
